import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe2, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * LanguageGreetingBanner — elegant, non-intrusive bottom-right pill that
 * detects the visitor's *browser* language and offers a one-tap switch
 * when it differs from the active site language.
 *
 * Design rules (per CEO directive):
 *   • Glassmorphism, indigo accent — must feel like a native part of LT.
 *   • Auto-shows once per visitor, ~1.4s after first paint (let the hero
 *     land first — no popup-fatigue).
 *   • Auto-dismisses after 12s if ignored.
 *   • Hard-dismissed once the user clicks the ✕ — never shown again.
 *   • Skips entirely if the user has already manually picked a language
 *     (LanguageContext exposes `autoDetected: false` in that case).
 *   • Skips if the browser language matches the current language.
 *   • Skips if the browser language is not one of our 7 supported.
 */
const LANG_NAME_NATIVE = {
  ar: 'العربية', en: 'English', es: 'Español',
  hi: 'हिन्दी',  ur: 'اردو',    fr: 'Français', ru: 'Русский',
};

const LANG_FLAG = {
  ar: '🇦🇪', en: '🇬🇧', es: '🇪🇸',
  hi: '🇮🇳', ur: '🇵🇰', fr: '🇫🇷', ru: '🇷🇺',
};

const PROMPTS = {
  ar: (n) => `يبدو أن لغتك هي ${n}. هل تريد التبديل؟`,
  en: (n) => `Looks like you speak ${n}. Switch?`,
  es: (n) => `Parece que hablas ${n}. ¿Cambiar?`,
  hi: (n) => `लगता है आप ${n} बोलते हैं. बदलें?`,
  ur: (n) => `لگتا ہے آپ ${n} بولتے ہیں۔ تبدیل کریں؟`,
  fr: (n) => `On dirait que vous parlez ${n}. Changer ?`,
  ru: (n) => `Похоже, вы говорите на ${n}. Переключить?`,
};

const CONFIRM = { ar: 'نعم', en: 'Yes', es: 'Sí', hi: 'हाँ', ur: 'ہاں', fr: 'Oui', ru: 'Да' };
const DISMISS_KEY = 'lang_greeting_dismissed_v1';

const detectBrowserLang = () => {
  try {
    const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const raw of candidates) {
      if (!raw) continue;
      const code = raw.toLowerCase().split('-')[0];
      if (LANG_NAME_NATIVE[code]) return code;
    }
  } catch { /* ignore */ }
  return null;
};

const LanguageGreetingBanner = () => {
  const { language, setLanguage, autoDetected } = useLanguage();
  const [target, setTarget] = useState(null);   // browser-detected lang
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Skip if user already explicitly chose a language (cookie/localStorage set)
    if (!autoDetected) return;
    // Skip if dismissed previously
    try { if (localStorage.getItem(DISMISS_KEY) === '1') return; } catch { return; }

    const browser = detectBrowserLang();
    if (!browser || browser === language) return;
    setTarget(browser);

    const showT = setTimeout(() => setShow(true), 1400);   // let hero land
    const hideT = setTimeout(() => setShow(false), 13400); // 12s on screen
    return () => { clearTimeout(showT); clearTimeout(hideT); };
  }, [language, autoDetected]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setShow(false);
  };

  const accept = () => {
    setLanguage(target);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setShow(false);
  };

  const isAr = language === 'ar';

  return (
    <AnimatePresence>
      {show && target && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.94 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed z-[150] bottom-5 ${isAr ? 'left-5' : 'right-5'}
            max-w-[calc(100vw-32px)] sm:max-w-md
            rounded-2xl overflow-hidden
            bg-[rgba(13,17,23,0.82)] backdrop-blur-2xl
            ring-1 ring-white/10 border border-white/5
            shadow-[0_24px_70px_rgba(2,6,23,0.55)]`}
          role="dialog"
          aria-live="polite"
          data-testid="language-greeting-banner"
        >
          <div className="relative px-4 sm:px-5 py-3.5 flex items-center gap-3">
            {/* Decorative glow */}
            <div
              className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-50"
              style={{ background: 'radial-gradient(circle, #6366F1 0%, transparent 70%)' }}
            />

            {/* Globe icon */}
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full
              bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shrink-0">
              <Globe2 className="w-4.5 h-4.5" />
            </span>

            {/* Prompt text */}
            <div className="min-w-0 flex-1 leading-tight">
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-indigo-300/80">
                Trust Ecosystem
              </p>
              <p className="text-[13px] sm:text-sm text-white/95 mt-0.5 truncate">
                <span className="me-1">{LANG_FLAG[target]}</span>
                {(PROMPTS[target] || PROMPTS.en)(LANG_NAME_NATIVE[target])}
              </p>
            </div>

            {/* Confirm pill */}
            <button
              type="button"
              onClick={accept}
              className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full
                bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-400 hover:to-violet-500
                text-white text-xs font-bold shadow-[0_6px_20px_rgba(79,70,229,0.5)]
                transition-all hover:scale-[1.04] active:scale-95"
              data-testid="language-greeting-accept"
            >
              {CONFIRM[language] || CONFIRM.en}
              <span className="text-[10px] font-mono opacity-70">{target.toUpperCase()}</span>
            </button>

            {/* Hard-dismiss */}
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full
                text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="language-greeting-dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LanguageGreetingBanner;
